import { mountCarsPane } from '../cars/CarsApp.js';
import { mountDriversPane } from '../drivers/DriversApp.js';
import { mountCircuitsPane } from '../circuits/CircuitsApp.js';
import type { GaragePaneHandle } from './garageEmbed.js';
import { mountModels3dPane } from './models3dPane.js';
import {
  buildGarageHash,
  GARAGE_CATEGORIES,
  GARAGE_CATEGORY_LABELS,
  parseGarageHash,
  type GarageCategory,
  type GarageRoute,
  DEFAULT_GARAGE_CATEGORY,
} from './garageHash.js';

export type GarageShellHandle = {
  /** Synchronise l’UI depuis le hash courant. */
  syncFromHash(): void;
  dispose(): void;
  /** Exposé pour les tests. */
  getRoute(): GarageRoute;
  getElement(): HTMLElement;
};

let tabsUid = 0;

/**
 * Shell garage : tabs catégories (centrés) · liste · détail.
 * Monte / démonte le panneau de la catégorie active.
 */
export function mountGarageShell(root: HTMLElement): GarageShellHandle {
  root.innerHTML = '';
  root.classList.add('is-garage-shell');

  const shell = document.createElement('div');
  shell.className = 'garage-shell';

  const tabsNav = document.createElement('nav');
  tabsNav.className = 'garage-shell__tabs';
  tabsNav.setAttribute('aria-label', 'Catégories du garage');

  const tabs = document.createElement('div');
  tabs.className = 'garage-tabs';
  tabs.style.setProperty('--tab-count', String(GARAGE_CATEGORIES.length));
  tabs.style.setProperty('--active-index', '0');
  tabs.style.setProperty('--hover-index', '0');

  const radioName = `garage-tabs-${++tabsUid}`;
  const tabInputs = new Map<GarageCategory, HTMLInputElement>();

  GARAGE_CATEGORIES.forEach((cat, i) => {
    const input = document.createElement('input');
    input.type = 'radio';
    input.className = 'garage-tabs__rd';
    input.name = radioName;
    input.id = `${radioName}-${cat}`;
    input.value = cat;
    input.dataset.category = cat;
    input.dataset.index = String(i);

    const label = document.createElement('label');
    label.className = 'garage-tabs__label';
    label.htmlFor = input.id;
    label.dataset.category = cat;
    label.style.setProperty('--index', String(i));
    const span = document.createElement('span');
    span.textContent = GARAGE_CATEGORY_LABELS[cat];
    label.appendChild(span);

    label.addEventListener('pointerenter', () => {
      tabs.style.setProperty('--hover-index', String(i));
    });
    label.addEventListener('pointerleave', () => {
      tabs.style.setProperty('--hover-index', tabs.style.getPropertyValue('--active-index') || '0');
    });

    input.addEventListener('change', () => {
      if (!input.checked) return;
      if (route.category === cat) return;
      const next: GarageRoute = { category: cat, id: null };
      const hash = buildGarageHash(next);
      ignoreHashChange = true;
      window.location.hash = hash;
      applyRoute(next);
      ignoreHashChange = false;
    });

    tabInputs.set(cat, input);
    tabs.append(input, label);
  });

  const bar = document.createElement('div');
  bar.className = 'garage-tabs__bar';
  bar.setAttribute('aria-hidden', 'true');
  const slidebar = document.createElement('div');
  slidebar.className = 'garage-tabs__slidebar';
  slidebar.setAttribute('aria-hidden', 'true');
  tabs.append(bar, slidebar);
  tabsNav.appendChild(tabs);

  const columns = document.createElement('div');
  columns.className = 'garage-shell__columns';

  const middle = document.createElement('div');
  middle.className = 'garage-shell__middle';

  const actionsHost = document.createElement('div');
  actionsHost.className = 'garage-shell__actions';

  const listHost = document.createElement('div');
  listHost.className = 'garage-shell__list';

  middle.append(actionsHost, listHost);

  const detailHost = document.createElement('div');
  detailHost.className = 'garage-shell__detail';

  columns.append(middle, detailHost);
  shell.append(tabsNav, columns);
  root.appendChild(shell);

  let route: GarageRoute = { category: DEFAULT_GARAGE_CATEGORY, id: null };
  let pane: GaragePaneHandle | null = null;
  let ignoreHashChange = false;

  function updateTabs(category: GarageCategory): void {
    const idx = GARAGE_CATEGORIES.indexOf(category);
    const activeIndex = idx >= 0 ? idx : 0;
    tabs.style.setProperty('--active-index', String(activeIndex));
    tabs.style.setProperty('--hover-index', String(activeIndex));
    tabInputs.forEach((input, cat) => {
      const on = cat === category;
      input.checked = on;
      const label = tabs.querySelector<HTMLLabelElement>(`label[data-category="${cat}"]`);
      if (on) {
        label?.setAttribute('aria-current', 'page');
      } else {
        label?.removeAttribute('aria-current');
      }
    });
  }

  /** Met à jour l’URL sans toucher `route` — seul `applyRoute` possède cet état. */
  function replaceHashQuiet(next: GarageRoute): void {
    const hash = buildGarageHash(next);
    const target = `#${hash}`;
    if (window.location.hash === target) return;
    ignoreHashChange = true;
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}${target}`);
    ignoreHashChange = false;
  }

  function mountCategory(next: GarageRoute): void {
    pane?.dispose();
    pane = null;
    listHost.innerHTML = '';
    detailHost.innerHTML = '';
    actionsHost.innerHTML = '';

    const hosts = {
      listHost,
      detailHost,
      actionsHost,
      modalRoot: root,
      initialId: next.id,
      onSelectId: (id: string | null): void => {
        const selected: GarageRoute = { category: next.category, id };
        replaceHashQuiet(selected);
        route = selected;
      },
    };

    switch (next.category) {
      case 'circuits':
        pane = mountCircuitsPane(hosts);
        break;
      case 'voitures':
        pane = mountCarsPane(hosts);
        break;
      case 'pilotes':
        pane = mountDriversPane(hosts);
        break;
      case 'modeles-3d':
        pane = mountModels3dPane(hosts);
        break;
    }
  }

  function applyRoute(next: GarageRoute): void {
    const categoryChanged = route.category !== next.category;
    const idChanged = route.id !== next.id;
    const needsMount = !pane || categoryChanged || idChanged;
    route = next;
    updateTabs(next.category);
    if (needsMount) {
      mountCategory(next);
    }
  }

  function syncFromHash(): void {
    const raw = window.location.hash.replace(/^#/, '').trim();
    const pathOnly = (raw.split('?')[0] ?? '').replace(/^\/+/, '');

    // `#garage` : restaurer la dernière catégorie si le shell est déjà monté,
    // sinon défaut circuits (évite tab Circuits + contenu Pilotes désynchronisés).
    if (pathOnly === 'garage') {
      const next: GarageRoute = pane
        ? { category: route.category, id: route.id }
        : { category: DEFAULT_GARAGE_CATEGORY, id: null };
      replaceHashQuiet(next);
      applyRoute(next);
      return;
    }

    const parsed = parseGarageHash(window.location.hash);
    if (!parsed) return;

    // Catégorie inconnue dans le hash : normaliser
    const parts = pathOnly.split('/').filter(Boolean);
    if (
      parts[0] === 'garage' &&
      parts[1] &&
      !(parts[1] in GARAGE_CATEGORY_LABELS) &&
      parsed.category === DEFAULT_GARAGE_CATEGORY
    ) {
      replaceHashQuiet(parsed);
    }

    applyRoute(parsed);
  }

  const onHashChange = (): void => {
    if (ignoreHashChange) return;
    if (
      parseGarageHash(window.location.hash) ||
      window.location.hash.replace(/^#/, '') === 'garage'
    ) {
      syncFromHash();
    }
  };
  window.addEventListener('hashchange', onHashChange);

  syncFromHash();

  return {
    syncFromHash,
    dispose(): void {
      window.removeEventListener('hashchange', onHashChange);
      pane?.dispose();
      pane = null;
      root.classList.remove('is-garage-shell');
      root.innerHTML = '';
    },
    getRoute(): GarageRoute {
      return { ...route };
    },
    getElement(): HTMLElement {
      return shell;
    },
  };
}
